import { useDocumentTitle } from 'client/utils/use-document-title';
import { rpx } from 'client/lib/rpx-client';
import { UserProfileIcon } from '@components/avatars';
import {
  IcoClock,
  IcoInfo,
  IcoVideoCamera,
  IcoExternalLink,
  IcoUsers,
  IcoChevronRight,
} from '@components/icons';
import { ruzcal } from 'shared/urls';
import { LoadedProps, RouteLoadProps } from '@components/router/async-router';
import { defCalRoute } from './common';
import { CalPublicLayout } from './cal-public-layout';
import { useMeetingType } from '@components/use-meeting-type';
import { EventTypeRow } from 'server/types/cal-schema';
import { useIntl } from 'shared/intl/use-intl';

type Props = LoadedProps<typeof load>;

export const route = defCalRoute({ isPublic: true, load, Page });

async function load(route: RouteLoadProps) {
  const { urlPrefix, hostId } = route.params;
  const result = await rpx.ruzcalEventTypes.getPublicEventTypes({
    urlPrefix,
    hostId,
  });
  return { ...result, host: { ...result.host, urlPrefix, hostId } };
}

function getEventTypeCardProps(
  evt: Pick<EventTypeRow, 'location' | 'locationDetail'>,
  meetingTypes: ReturnType<typeof useMeetingType>,
) {
  if (evt.location === 'zoom') {
    return {
      Icon: IcoVideoCamera,
      locationText: meetingTypes.zoom,
      bg: 'bg-teal-100',
      textStyle: 'text-teal-700',
      hoverStyle: 'hover:border-teal-500 focus:border-teal-500 hover:shadow-lg',
    };
  }

  if (evt.location !== 'external') {
    return {
      Icon: IcoVideoCamera,
      locationText: meetingTypes.videoconference,
      bg: 'bg-teal-100',
      textStyle: 'text-teal-700',
      hoverStyle: 'hover:border-teal-500 focus:border-teal-500 hover:shadow-lg',
    };
  }

  if (evt.locationDetail?.external.startsWith('http')) {
    return {
      Icon: IcoExternalLink,
      locationText: meetingTypes.external,
      bg: 'bg-orange-100',
      textStyle: 'text-orange-700',
      hoverStyle: 'hover:border-orange-500 focus:border-orange-500 hover:shadow-lg',
    };
  }

  return {
    Icon: IcoUsers,
    locationText: meetingTypes.inperson,
    bg: 'bg-indigo-100',
    textStyle: 'text-indigo-700',
    hoverStyle: 'hover:border-indigo-500 focus:border-indigo-500 hover:shadow-lg',
  };
}

function Page({ state: { host, eventTypes } }: Props) {
  useDocumentTitle([host.name, 'Events']);
  const meetingTypes = useMeetingType();
  const intl = useIntl();

  return (
    <CalPublicLayout>
      <section class="p-8 bg-white rounded-2xl max-w-full w-2xl shadow-lg an-scale-in">
        <div class="flex flex-col items-center justify-center gap-4 py-4">
          <UserProfileIcon user={host} size="size-20" />
          <div class="flex flex-col items-center leading-5">
            <span class="text-gray-600">{intl('Book a meeting with')}</span>
            <span class="text-xl font-semibold text-gray-900">{intl(host.name)}</span>
          </div>
        </div>

        <div class="flex flex-col gap-6 pt-4">
          <h2 class="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
            {intl('Choose an event type to book')}
          </h2>

          {!eventTypes.length && (
            <section class="text-center text-gray-500">
              {intl('There are currently no events available.')}
            </section>
          )}

          {!!eventTypes.length && (
            <section class="flex flex-col gap-4">
              {eventTypes.map((evt) => {
                const bookingUrl = host.hostId
                  ? ruzcal.directBookingUrl({ hostId: host.hostId, eventTypeId: evt.id })
                  : ruzcal.newBookingUrl({ urlPrefix: host.urlPrefix, urlSuffix: evt.urlSuffix });

                const {
                  Icon: EventTypeIcon,
                  locationText,
                  bg,
                  textStyle,
                  hoverStyle,
                } = getEventTypeCardProps(evt, meetingTypes);

                return (
                  <a
                    href={bookingUrl}
                    class={`block bg-white border-2 border-gray-200 rounded-xl shadow-sm ${hoverStyle} transition-all duration-200`}
                    key={evt.id}
                  >
                    <div class="flex items-center justify-between p-4">
                      <div class="flex items-start gap-4">
                        <div
                          class={`flex-shrink-0 size-12 rounded-xl flex items-center justify-center ${bg} ${textStyle}`}
                        >
                          <EventTypeIcon class="size-7" />
                        </div>

                        <div class="flex flex-col flex-grow">
                          <span class="font-semibold text-gray-900">{evt.name}</span>

                          <div class="flex items-center gap-3 text-sm text-gray-500 mt-1">
                            <span class="inline-flex items-center gap-1">
                              <IcoClock class="size-4" /> {evt.duration}m
                            </span>

                            <span class="inline-flex items-center gap-1">
                              <EventTypeIcon class="size-4" />
                              {locationText}
                            </span>
                          </div>

                          {evt.description && (
                            <span class="inline-flex items-start gap-1 text-sm text-gray-600 mt-2">
                              <IcoInfo class="size-4 flex-shrink-0 mt-0.5" />
                              <span class="truncate">{evt.description}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <IcoChevronRight class="size-5 text-gray-500 flex-shrink-0" />
                    </div>
                  </a>
                );
              })}
            </section>
          )}
        </div>
      </section>
    </CalPublicLayout>
  );
}
